// Zeiträume in Wartungsverträgen (Wartungsrhythmus, Kündigungsfrist, Verlängerung).
//
// Problem vorher: „12 Monate“ und „1 Jahr“ waren zwei verschiedene Eingaben für
// dieselbe Sache. Hier gibt es dafür genau eine kanonische Form plus lesbare
// Labels und Voreinstellungen, die das Formular als Chips anbietet.

export type PeriodUnit = "days" | "weeks" | "months" | "years";

export interface Period {
  value: number;
  unit: PeriodUnit;
}

export const ALL_PERIOD_UNITS: readonly PeriodUnit[] = ["days", "weeks", "months", "years"];
/** Backend erlaubt für Kündigungsfristen keine Jahre. */
export const NOTICE_PERIOD_UNITS: readonly PeriodUnit[] = ["days", "weeks", "months"];
/** Backend erlaubt für Verlängerungslaufzeiten nur Monate und Jahre. */
export const RENEWAL_TERM_UNITS: readonly PeriodUnit[] = ["months", "years"];

export const PERIOD_UNIT_LABELS: Record<PeriodUnit, { one: string; many: string }> = {
  days: { one: "Tag", many: "Tage" },
  weeks: { one: "Woche", many: "Wochen" },
  months: { one: "Monat", many: "Monate" },
  years: { one: "Jahr", many: "Jahre" },
};

export function isPeriodUnit(value: unknown): value is PeriodUnit {
  return typeof value === "string" && (ALL_PERIOD_UNITS as readonly string[]).includes(value);
}

export function toPeriod(value: unknown, unit: unknown): Period | null {
  const v = Number(value);
  if (!Number.isFinite(v) || v <= 0 || !isPeriodUnit(unit)) return null;
  return { value: Math.round(v), unit };
}

/**
 * Vergleichsschlüssel: Monate/Jahre werden in Monaten, Tage/Wochen in Tagen
 * verglichen. Damit gilt 12 Monate ≡ 1 Jahr und 14 Tage ≡ 2 Wochen.
 */
export function periodKey(p: Period | null | undefined): string {
  if (!p) return "";
  if (p.unit === "years") return `m:${p.value * 12}`;
  if (p.unit === "months") return `m:${p.value}`;
  if (p.unit === "weeks") return `d:${p.value * 7}`;
  return `d:${p.value}`;
}

export function periodsEqual(a: Period | null | undefined, b: Period | null | undefined): boolean {
  return Boolean(a && b) && periodKey(a) === periodKey(b);
}

/**
 * Kanonische Darstellung innerhalb der erlaubten Einheiten:
 * volle Jahre als Jahre (wenn erlaubt), sonst Monate; volle Wochen als Wochen, sonst Tage.
 */
export function canonicalPeriod(p: Period, allowed: readonly PeriodUnit[] = ALL_PERIOD_UNITS): Period {
  if (p.unit === "months" || p.unit === "years") {
    const months = p.unit === "years" ? p.value * 12 : p.value;
    if (allowed.includes("years") && months % 12 === 0) return { value: months / 12, unit: "years" };
    if (allowed.includes("months")) return { value: months, unit: "months" };
    return { value: months / 12, unit: "years" };
  }
  const days = p.unit === "weeks" ? p.value * 7 : p.value;
  if (allowed.includes("weeks") && days % 7 === 0) return { value: days / 7, unit: "weeks" };
  if (allowed.includes("days")) return { value: days, unit: "days" };
  return { value: days / 7, unit: "weeks" };
}

/** „1 Jahr“, „3 Monate“, „4 Wochen“ – für Fristen und Laufzeiten. */
export function durationLabel(p: Period | null | undefined): string {
  if (!p) return "–";
  const c = canonicalPeriod(p);
  const labels = PERIOD_UNIT_LABELS[c.unit];
  return `${c.value} ${c.value === 1 ? labels.one : labels.many}`;
}

/** „jährlich“, „halbjährlich“, „alle 2 Jahre“ – für den Wartungsrhythmus. */
export function frequencyLabel(p: Period | null | undefined): string {
  if (!p) return "–";
  const c = canonicalPeriod(p);
  if (c.unit === "years") return c.value === 1 ? "jährlich" : `alle ${c.value} Jahre`;
  if (c.unit === "months") {
    if (c.value === 1) return "monatlich";
    if (c.value === 3) return "quartalsweise";
    if (c.value === 6) return "halbjährlich";
    return `alle ${c.value} Monate`;
  }
  if (c.unit === "weeks") return c.value === 1 ? "wöchentlich" : `alle ${c.value} Wochen`;
  return c.value === 1 ? "täglich" : `alle ${c.value} Tage`;
}

export interface PeriodPreset {
  key: string;
  label: string;
  period: Period;
}

export const INTERVAL_PRESETS: readonly PeriodPreset[] = [
  { key: "monthly", label: "Monatlich", period: { value: 1, unit: "months" } },
  { key: "quarterly", label: "Quartalsweise", period: { value: 3, unit: "months" } },
  { key: "half_yearly", label: "Halbjährlich", period: { value: 6, unit: "months" } },
  { key: "yearly", label: "Jährlich", period: { value: 1, unit: "years" } },
  { key: "two_yearly", label: "Alle 2 Jahre", period: { value: 2, unit: "years" } },
];

export const NOTICE_PRESETS: readonly PeriodPreset[] = [
  { key: "4w", label: "4 Wochen", period: { value: 4, unit: "weeks" } },
  { key: "1m", label: "1 Monat", period: { value: 1, unit: "months" } },
  { key: "3m", label: "3 Monate", period: { value: 3, unit: "months" } },
  { key: "6m", label: "6 Monate", period: { value: 6, unit: "months" } },
];

export const RENEWAL_PRESETS: readonly PeriodPreset[] = [
  { key: "6m", label: "6 Monate", period: { value: 6, unit: "months" } },
  { key: "1y", label: "1 Jahr", period: { value: 1, unit: "years" } },
  { key: "2y", label: "2 Jahre", period: { value: 2, unit: "years" } },
];

export function matchPreset(presets: readonly PeriodPreset[], p: Period | null | undefined): PeriodPreset | null {
  if (!p) return null;
  return presets.find((preset) => periodsEqual(preset.period, p)) ?? null;
}
